'use client';

import MemoizedReactMarkdown from '@/ui/chat/memoized-markdown';
import ReplicaThinkingIndicator from '@/ui/replica-thinking-indicator';
import dynamic from 'next/dynamic';
import { memo } from 'react';
import type { FC } from 'react';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { twMerge } from 'tailwind-merge';
import CopyToClipboard from '../copy-to-clipboard';
interface ChatMessageProps {
  content: string;
  className?: string;
  ref?: React.RefObject<HTMLDivElement | null>;
}
const CodeHighlighter = dynamic(() => import('./code-highlighter'), {
  ssr: false
});
const remarkPlugins = [remarkGfm];
const COMPONENTS: Components = {
  h1({
    children
  }) {
    return <h1 className="pb-2 pt-2 text-xl font-medium">{children}</h1>;
  },
  h2({
    children
  }) {
    return <h2 className="pb-2 pt-2 text-lg font-medium">{children}</h2>;
  },
  h3({
    children
  }) {
    return <h3 className="pb-2 pt-2 text-base font-medium">{children}</h3>;
  },
  p({
    children
  }) {
    return <p className={'text-[1.025rem] md:text-[1.125rem] leading-[1.5rem] md:leading-[1.7rem] mb-2 max-w-full whitespace-break-spaces last:mb-0 max-w-prose'}>
        {children}
      </p>;
  },
  ul({
    children
  }) {
    return <ul className="my-0 text-[1.025rem] md:text-[1.125rem] leading-[1.5rem] max-w-prose">{children}</ul>;
  },
  ol({
    children
  }) {
    return <ol className="my-0 text-[1.025rem] md:text-[1.125rem] leading-[1.5rem] max-w-prose">{children}</ol>;
  },
  a({
    children,
    ...props
  }) {
    return <a href={props.href} target="_blank" className="font-bold text-primary underline underline-offset-2 transition-opacity duration-300 hover:opacity-70" rel="noopener noreferrer">
        {children}
      </a>;
  },
  code(props) {
    const {
      children,
      className,
      ...rest
    } = props;
    const match = /language-(\w+)/.exec(className || '');
    return match ? <CodeHighlighter {...rest} ref={rest.ref} language={match[1]}>
        {String(children).replace(/\n$/, '')}
      </CodeHighlighter> : <code {...rest} className={className}>
        {children}
      </code>;
  }
};
export const ThinkingSkeleton: FC<Pick<ChatMessageProps, 'className' | 'ref'>> = ({
  className,
  ref
}) => {
  return <div ref={ref} className="group flex flex-col gap-1" data-sentry-component="ThinkingSkeleton" data-sentry-source-file="agent-message.tsx">
      <div className={twMerge('flex flex-col gap-3 py-2 px-3 rounded-lg', 'bg-foreground/3 text-foreground rounded-bl-none self-start mr-8', className)}>
        <ReplicaThinkingIndicator data-sentry-element="ReplicaThinkingIndicator" data-sentry-source-file="agent-message.tsx" />
      </div>
    </div>;
};
export default memo(function AgentChatMessage({
  className,
  content,
  ref
}: ChatMessageProps) {
  const isCSR = typeof window !== 'undefined' && 'ontouchstart' in window;
  return <div ref={ref} className="group flex flex-col gap-1">
      <div className={twMerge('flex flex-col gap-3 rounded-lg pl-1.5 pr-2.5 text-foreground rounded-bl-none self-start', className)}>
        <MemoizedReactMarkdown className={'prose dark:prose-invert prose-p:leading-relaxed prose-pre:p-0 break-words'} remarkPlugins={remarkPlugins} components={COMPONENTS}>
          {content}
        </MemoizedReactMarkdown>
      </div>

      <div className={twMerge('group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex gap-2 ml-2', isCSR ? 'visible opacity-100' : '')}>
        <CopyToClipboard alignTooltipEnd={false} content={content} />
      </div>
    </div>;
});